import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ActivityPrice {
  id: string;
  activity_id: string;
  price_amount: number;
  currency: string;
  effective_from: string;
  effective_until: string | null;
  is_active: boolean;
  billing_type_target: 'per_session' | 'monthly';
  activity?: {
    id: string;
    name: string;
  };
}

export interface BillingPeriod {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  status: string;
  created_at: string;
}

export interface StudentBilling {
  id: string;
  student_id: string;
  billing_period_id: string;
  activity_id: string;
  billing_type: 'per_session' | 'monthly';
  sessions_attended: number;
  price_amount: number;
  total_amount: number;
  status: string;
  invoice_date: string;
  due_date: string;
  paid_date: string | null;
  notes: string | null;
  student?: {
    id: string;
    name: string;
    class: string;
  };
  activity?: {
    id: string;
    name: string;
  };
}

export interface AttendanceReport {
  activity_id: string;
  activity_name: string;
  student_id: string;
  student_name: string;
  student_class: string;
  total_sessions: number;
  price_amount: number;
  total_amount: number;
}

export const useActivityPrices = () => {
  const [prices, setPrices] = useState<ActivityPrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPrices = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('activity_prices')
        .select(`
          *,
          activities(id, name)
        `)
        .eq('is_active', true)
        .order('effective_from', { ascending: false });

      if (error) throw error;
      setPrices(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPrices();
  }, []);

  return { prices, loading, error, refetch: loadPrices };
};

export const useBillingPeriods = () => {
  const [periods, setPeriods] = useState<BillingPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPeriods = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('billing_periods')
        .select('*')
        .order('start_date', { ascending: false });

      if (error) throw error;
      setPeriods(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPeriods();
  }, []);

  return { periods, loading, error, refetch: loadPeriods };
};

export const useStudentBilling = (billingPeriodId?: string) => {
  const [billing, setBilling] = useState<StudentBilling[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadBilling = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let query = supabase
        .from('student_billing')
        .select(`
          id,
          student_id,
          billing_period_id,
          activity_id,
          billing_type,
          sessions_attended,
          price_amount,
          total_amount,
          status,
          invoice_date,
          due_date,
          paid_date,
          notes,
          created_at,
          updated_at,
          student:students(id, name, class),
          activity:activities(id, name)
        `)
        .order('invoice_date', { ascending: false });

      if (billingPeriodId) {
        query = query.eq('billing_period_id', billingPeriodId);
      }

      const { data, error } = await query;
      if (error) {
        throw error;
      }
      
      setBilling(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBilling();
  }, [billingPeriodId]);

  return { billing, loading, error, refetch: loadBilling };
};

export const useAttendanceReport = (startDate: string, endDate: string) => {
  const [report, setReport] = useState<AttendanceReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateReport = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Obtener asistencias en el período especificado
      const { data: attendances, error: attendanceError } = await supabase
        .from('attendances')
        .select(`
          activity_id,
          student_id,
          activities(id, name),
          students(id, name, class)
        `)
        .gte('date', startDate)
        .lte('date', endDate)
        .eq('status', 'present');

      if (attendanceError) throw attendanceError;

      // Obtener precios de actividades
      const { data: prices, error: pricesError } = await supabase
        .from('activity_prices')
        .select('*')
        .eq('is_active', true);

      if (pricesError) throw pricesError;

      // Agrupar asistencias por estudiante y actividad
      const groupedAttendances: Record<string, AttendanceReport> = {};

      attendances?.forEach((attendance: any) => {
        const key = `${attendance.student_id}-${attendance.activity_id}`;
        const price = prices?.find(p => p.activity_id === attendance.activity_id);
        
        if (!groupedAttendances[key]) {
          groupedAttendances[key] = {
            activity_id: attendance.activity_id,
            activity_name: attendance.activities?.name || 'Actividad desconocida',
            student_id: attendance.student_id,
            student_name: attendance.students?.name || 'Estudiante desconocido',
            student_class: attendance.students?.class || '',
            total_sessions: 0,
            price_amount: price?.price_amount || 0,
            total_amount: 0
          };
        }
        
        groupedAttendances[key].total_sessions += 1;
        groupedAttendances[key].total_amount = groupedAttendances[key].total_sessions * groupedAttendances[key].price_amount;
      });

      setReport(Object.values(groupedAttendances));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return { report, loading, error, generateReport };
};

// CRUD Functions for Finance
export const createActivityPrice = async (priceData: {
  activity_id: string;
  price_amount: number;
  billing_type_target: 'per_session' | 'monthly';
  currency?: string;
  effective_from: string;
  effective_until?: string;
}) => {
  const { data, error } = await supabase
    .from('activity_prices')
    .insert(priceData)
    .select();
  
  if (error) throw error;
  return data;
};

export const updateActivityPrice = async (id: string, priceData: {
  price_amount?: number;
  effective_until?: string;
  is_active?: boolean;
}) => {
  const { data, error } = await supabase
    .from('activity_prices')
    .update(priceData)
    .eq('id', id)
    .select();
  
  if (error) throw error;
  return data;
};

export const deactivateActivityPrice = async (id: string) => {
  const { data, error } = await supabase
    .from('activity_prices')
    .update({ is_active: false })
    .eq('id', id)
    .select();
  
  if (error) throw error;
  return data;
};

export const createBillingPeriod = async (periodData: {
  name: string;
  start_date: string;
  end_date: string;
  status?: string;
}) => {
  const { data, error } = await supabase
    .from('billing_periods')
    .insert(periodData)
    .select();
  
  if (error) throw error;
  return data;
};

export const updateBillingPeriod = async (id: string, updateData: {
  status?: string;
  name?: string;
  start_date?: string;
  end_date?: string;
}) => {
  const { data, error } = await supabase
    .from('billing_periods')
    .update(updateData)
    .eq('id', id)
    .select();
  
  if (error) throw error;
  return data;
};

export const updateStudentBilling = async (id: string, updateData: {
  status?: string;
  paid_date?: string | null;
  notes?: string;
}) => {
  const { data, error } = await supabase
    .from('student_billing')
    .update({ ...updateData, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select();
  
  if (error) throw error;
  return data;
};

// Función para actualizar el billing_type_override de un estudiante en una actividad
export const updateStudentBillingType = async (
  studentId: string, 
  activityId: string, 
  billingTypeOverride: 'per_session' | 'monthly' | null
) => {
  const { data, error } = await supabase
    .from('student_activities')
    .update({ billing_type_override: billingTypeOverride })
    .eq('student_id', studentId)
    .eq('activity_id', activityId)
    .select();
  
  if (error) throw error;
  return data;
};

export const generateBillingForPeriod = async (
  billingPeriodId: string,
  startDate: string,
  endDate: string
) => {
  const generationStats = {
    processed: 0,
    skipped: 0,
    errors: 0,
    newInvoices: 0,
    totalAmount: 0,
    details: [] as any[]
  };

  try {

    // PASO 1: Verificar facturas YA EXISTENTES para el período
    const { data: existingBilling, error: existingError } = await supabase
      .from('student_billing')
      .select('student_id, activity_id, id, total_amount')
      .eq('billing_period_id', billingPeriodId);

    if (existingError) throw existingError;

    const existingKeys = new Set(
      existingBilling?.map(bill => `${bill.student_id}-${bill.activity_id}`) || []
    );

    

    // PASO 2: Obtener actividades (simplificadas)
    const { data: activities, error: activitiesError } = await supabase
      .from('activities')
      .select('id, name')
      .eq('status', 'active');

    if (activitiesError) throw activitiesError;

    const activitiesMap = new Map(activities?.map(a => [a.id, a]) || []);

    // PASO 3: Obtener precios de actividades activos (ambos tipos)
    const { data: prices, error: pricesError } = await supabase
      .from('activity_prices')
      .select('*')
      .eq('is_active', true);

    if (pricesError) throw pricesError;

    // Crear mapas de precios por tipo de facturación
    const sessionPricesMap = new Map();
    const monthlyPricesMap = new Map();
    
    prices?.forEach(p => {
      if (p.billing_type_target === 'per_session') {
        sessionPricesMap.set(p.activity_id, p);
      } else if (p.billing_type_target === 'monthly') {
        monthlyPricesMap.set(p.activity_id, p);
      }
    });

    // PASO 4: Obtener estudiantes inscritos en actividades CON BILLING_TYPE_OVERRIDE Y DEFAULT_BILLING_TYPE
    const { data: enrollments, error: enrollmentsError } = await supabase
      .from('student_activities')
      .select(`
        student_id,
        activity_id,
        billing_type_override,
        students(id, name, first_name, last_name, default_billing_type)
      `)
      .eq('status', 'active');

    if (enrollmentsError) throw enrollmentsError;

    

    // PASO 5: Para actividades POR SESIÓN, obtener asistencias
    const { data: attendances, error: attendanceError } = await supabase
      .from('attendances')
      .select('activity_id, student_id, date')
      .gte('date', startDate)
      .lte('date', endDate)
      .eq('status', 'present');

    if (attendanceError) throw attendanceError;

    // Agrupar asistencias por estudiante-actividad
    const attendancesByKey = new Map<string, number>();
    attendances?.forEach((att: any) => {
      const key = `${att.student_id}-${att.activity_id}`;
      attendancesByKey.set(key, (attendancesByKey.get(key) || 0) + 1);
    });

    

    // PASO 6: Procesar cada inscripción
    const billingRecords: any[] = [];

    for (const enrollment of enrollments || []) {
      const { student_id, activity_id } = enrollment;
      const key = `${student_id}-${activity_id}`;
      
      generationStats.processed++;

      // Verificar si YA EXISTE factura para esta combinación
      if (existingKeys.has(key)) {
        generationStats.skipped++;
        generationStats.details.push({
          student: enrollment.students?.name || 'Desconocido',
          activity: activitiesMap.get(activity_id)?.name || 'Desconocida',
          status: 'skipped',
          reason: 'Ya facturado'
        });
        continue;
      }

      const activity = activitiesMap.get(activity_id);
      
      if (!activity) {
        generationStats.errors++;
        generationStats.details.push({
          student: enrollment.students?.name || 'Desconocido',
          activity: 'Desconocida',
          status: 'error',
          reason: 'Actividad no encontrada'
        });
        continue;
      }

      // DETERMINAR TIPO DE FACTURACIÓN EFECTIVO CON JERARQUÍA CORREGIDA
      const studentDefaultBilling = enrollment.students?.default_billing_type || 'per_session';
      const effectiveBillingType = enrollment.billing_type_override || studentDefaultBilling;
      
      
      // Obtener precio según el tipo de facturación efectivo con fallback
      let price = effectiveBillingType === 'per_session' 
        ? sessionPricesMap.get(activity_id)
        : monthlyPricesMap.get(activity_id);
      
      let actualBillingType = effectiveBillingType;
      let fallbackUsed = false;

      // FALLBACK: Si prefiere per_session pero no hay precio, intentar usar monthly
      if (!price && effectiveBillingType === 'per_session') {
        price = monthlyPricesMap.get(activity_id);
        if (price) {
          actualBillingType = 'monthly';
          fallbackUsed = true;
          
        }
      }

      // Si aún no hay precio después del fallback, es un error
      if (!price) {
        generationStats.errors++;
        generationStats.details.push({
          student: enrollment.students?.name || 'Desconocido',
          activity: activity.name,
          status: 'error',
          reason: `Sin precio ${effectiveBillingType === 'per_session' ? 'por sesión ni mensual' : 'mensual'} configurado`
        });
        
        continue;
      }

      let sessionsAttended = 0;
      let totalAmount = 0;

      // LÓGICA DUAL DE FACTURACIÓN CON OVERRIDE (usar actualBillingType después del fallback)
      if (actualBillingType === 'per_session') {
        // FACTURACIÓN POR SESIÓN: sesiones × precio
        sessionsAttended = attendancesByKey.get(key) || 0;
        totalAmount = sessionsAttended * price.price_amount;
        
        // Solo crear factura si hay asistencias
        if (sessionsAttended === 0) {
          generationStats.skipped++;
          generationStats.details.push({
            student: enrollment.students?.name || 'Desconocido',
            activity: activity.name,
            status: 'skipped',
            reason: 'Sin asistencias registradas'
          });
          
          continue;
        }
        
        
      } else {
        // FACTURACIÓN MENSUAL: 1 × cuota_mensual (independiente de asistencias)
        sessionsAttended = 1; // Siempre 1 para cuotas mensuales
        totalAmount = price.price_amount;
        
      }

      // Crear registro de facturación
      const billingRecord = {
        student_id,
        activity_id,
        billing_period_id: billingPeriodId,
        billing_type: actualBillingType,
        sessions_attended: sessionsAttended,
        price_amount: price.price_amount,
        total_amount: totalAmount,
        status: 'pending',
        invoice_date: new Date().toISOString().split('T')[0],
        due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        notes: fallbackUsed ? `Facturación mensual aplicada (precio por sesión no disponible)` : null
      };

      billingRecords.push(billingRecord);
      generationStats.newInvoices++;
      generationStats.totalAmount += totalAmount;
      
      generationStats.details.push({
        student: enrollment.students?.name || 'Desconocido',
        activity: activity.name,
        billing_type: effectiveBillingType,
        sessions: sessionsAttended,
        amount: totalAmount,
        status: 'created'
      });
    }

    // PASO 7: Insertar nuevas facturas (si las hay)
    let insertedData = null;
    if (billingRecords.length > 0) {
      
      const { data, error } = await supabase
        .from('student_billing')
        .insert(billingRecords)
        .select();

      if (error) {
        throw error;
      }
      
      insertedData = data;
    }
    
    return {
      success: true,
      data: insertedData,
      stats: generationStats,
      message: `Procesados: ${generationStats.processed} | Nuevos: ${generationStats.newInvoices} | Omitidos: ${generationStats.skipped} | Errores: ${generationStats.errors}`
    };

  } catch (error) {
    generationStats.errors++;
    
    return {
      success: false,
      error: error,
      stats: generationStats,
      message: `Error durante la generación: ${error instanceof Error ? error.message : 'Error desconocido'}`
    };
  }
};
